import { DialogContent } from "components/Dialog";
import { useEffect, useState } from "react";
import { Button, ButtonColor, ButtonSize } from "components/Button";
import PaymentMethods from "../../../../../../../../components/PaymentMethods/PaymentMethods";
import { APIResponse, PageStatus } from "types";
import SuccessDialog from "components/SuccessDialog";
import {
  PaymentsService,
  StudentToOrgTransactionType,
  PaymentMethodType,
  StudentLimitedResponse,
  CreditCardResponse,
  ACHResponse,
} from "client/openapi";
import Notifications from "util/notifications";
import CurrencyInput, { CurrencyInputProps } from "react-currency-input-field";
import ErrorDialog from "components/ErrorDialog";

export default function OneOffChargeModal({
  orgId,
  student,
}: {
  orgId: number;
  student: StudentLimitedResponse;
}) {
  const [status, setStatus] = useState<PageStatus>(PageStatus.EDITING);
  const [amount, setAmount] = useState<number>(0);
  const [reason, setReason] = useState<string>("");
  const [invalidPrice, setInvalidPrice] = useState<boolean>(true);
  const [invalidReason, setInvalidReason] = useState<boolean>(true);
  const [error, setError] = useState<APIResponse>();
  const [paymentMethod, setPaymentMethod] = useState<
    CreditCardResponse | ACHResponse | string
  >();
  const [paymentMethodType, setPaymentMethodType] =
    useState<PaymentMethodType>();
  const [otherPaymentDescription, setOtherPaymentDescription] =
    useState<string>("");

  const [
    makeSureUserDoesntSpamChargeButton,
    setMakeSureUserDoesntSpamChargeButton,
  ] = useState<boolean>(false);

  const handleCharge = async () => {
    setMakeSureUserDoesntSpamChargeButton(true);

    // value is over limit
    if (Number(amount / 100) > 10000) {
      setInvalidPrice(true);
      setError({
        message: "Please enter a value less than or equal to $10,000.00",
      });
      setMakeSureUserDoesntSpamChargeButton(false);
      return;
    }

    // value is under limit
    if (Number(amount / 100) < 5) {
      setInvalidPrice(true);
      setError({
        message: "Please charge for an amount greater than or equal to $5.00",
      });
      setMakeSureUserDoesntSpamChargeButton(false);
      return;
    }

    // no amount added
    if (
      amount === 0 ||
      amount === null ||
      amount === undefined ||
      Number.isNaN(Number(amount))
    ) {
      setInvalidPrice(true);
      setError({
        message: "Please enter an amount for this charge",
      });
      setMakeSureUserDoesntSpamChargeButton(false);
      return;
    }

    // no reason added
    if (reason === "" || reason === null || reason === undefined) {
      setInvalidReason(true);
      setError({
        message: "Please enter a reason for this charge",
      });
      setMakeSureUserDoesntSpamChargeButton(false);
      return;
    }

    // shorter reason needed
    if (reason.length >= 100) {
      setInvalidReason(true);
      setError({
        message: "Please enter a shorter reason for this charge",
      });
      setMakeSureUserDoesntSpamChargeButton(false);
      return;
    }

    if (!paymentMethodType || !paymentMethod) {
      setError({ message: "Please select a payment method" });
      setMakeSureUserDoesntSpamChargeButton(false);
      return;
    }

    await PaymentsService.createStudentToOrgPayment({
      requestBody: {
        amount: amount,
        org_id: orgId,
        student_id: student.id,
        payment_method_id:
          paymentMethodType === PaymentMethodType.CARD ||
          paymentMethodType === PaymentMethodType.ACH
            ? (paymentMethod as CreditCardResponse | ACHResponse)
                .payment_method_id
            : undefined,
        invoice_email:
          paymentMethodType === PaymentMethodType.INVOICE
            ? (paymentMethod as string)
            : undefined,
        external_payment_method_source:
          paymentMethodType === PaymentMethodType.EXTERNAL_PAYMENT_METHOD
            ? (paymentMethod as string)
            : undefined,
        payment_method_type: paymentMethodType,
        return_url: `${process.env.REACT_APP_FRONTEND_DOMAIN}/manage/students/${student.id}#billing`,
        reason: otherPaymentDescription
          ? `${reason} - Paid via ${otherPaymentDescription}`
          : reason,
        student_to_org_transaction_type: StudentToOrgTransactionType.DIRECT,
      },
    })
      .then(() => {
        setStatus(PageStatus.SUCCESS);
      })
      .catch((err) => {
        setError(err);
        setStatus(PageStatus.ERROR);
      });
  };

  const verifyPricingEdit: CurrencyInputProps["onValueChange"] = (
    value,
    name
  ) => {
    // value is over limit
    if (Number(value) > 10000) {
      setInvalidPrice(true);
      setError({
        message: "Please enter a value less than or equal to $10,000.00",
      });
      return;
    }

    // value is under limit
    if (Number(value) <= 0) {
      setInvalidPrice(true);
      setError({
        message: "Amount must be greater than $5.00!",
      });
      return;
    }

    // value is under limit
    if (
      Number(value) === null ||
      Number(value) === undefined ||
      Number.isNaN(Number(value))
    ) {
      setInvalidPrice(true);
      setError({
        message: "Amount must be greater than $5.00!",
      });
      return;
    }

    setInvalidPrice(false);
    setAmount(Number(value) * 100);
    setMakeSureUserDoesntSpamChargeButton(false);
  };

  const verifyReason = (reason) => {
    // no reason added
    if (reason === "") {
      setInvalidReason(true);
      setError({
        message: "Please enter a reason for this charge",
      });
      return;
    }

    // shorter reason needed
    if (reason.length >= 100) {
      setInvalidReason(true);
      setError({
        message: "Please enter a shorter reason",
      });
      return;
    }

    setInvalidReason(false);
    setReason(reason);
    setMakeSureUserDoesntSpamChargeButton(false);
  };

  useEffect(() => {
    error &&
      Notifications.error(error?.message || "An unexpected error occurred.");
  }, [error]);

  const reset = () => {
    setPaymentMethod("");
    setPaymentMethodType(undefined);
    setOtherPaymentDescription("");
    setAmount(0);
    setReason("");
    setInvalidPrice(true);
    setInvalidReason(true);
    setMakeSureUserDoesntSpamChargeButton(false);
  };

  if (status === PageStatus.SUCCESS) {
    return (
      <SuccessDialog
        message="Attempting charge. Reload the page to see your changes!"
        onClose={() => {
          reset();
          setStatus(PageStatus.EDITING);
        }}
      />
    );
  }

  if (status === PageStatus.ERROR) {
    return (
      <ErrorDialog
        message="Charge cannot be created at this time"
        onClose={() => {
          reset();
          setStatus(PageStatus.EDITING);
        }}
      />
    );
  }

  return (
    <DialogContent className="dialog-content max-w-[800px]" onClose={reset}>
      <div className="mt-3 font-semibold text-center header text-lg">
        Make a one-off charge
      </div>

      <div className="mt-3 border border-gray-300 border-2 rounded-lg p-4 flex gap-3">
        <div className="w-6/12">
          <p className="text-sm font-bold ">Amount</p>
          <CurrencyInput
            className="input w-11/12 h-3/6"
            id="amount"
            name="amount"
            placeholder={`$100.00`}
            prefix={"$"}
            decimalsLimit={2}
            allowNegativeValue={false}
            onValueChange={verifyPricingEdit}
          />
        </div>

        <div className="w-6/12">
          <p className="text-sm font-bold" style={{ marginBottom: "7px" }}>
            Reason
          </p>
          <input
            type="text"
            className="text-sm header w-full border border-black mb-2 rounded py-0.5 px-1 border-gray-300 h-9 w-11/12"
            placeholder="What's this charge for?"
            onChange={(e) => verifyReason(e.target.value)}
          />
        </div>
      </div>

      <div className="mt-3 border border-gray-300 border-2 rounded-lg p-4">
        <p className="mb-2 text-sm font-bold">Bill to:</p>
        <div className="px-2">
          <PaymentMethods
            selectable={true}
            showOptionToAddExternalPaymentMethodSource={true}
            studentIfInitiatedByAdmin={student}
            setPaymentMethod={setPaymentMethod}
            setPaymentMethodType={setPaymentMethodType}
          />
        </div>
      </div>

      <div className="w-full text-center mt-6">
        <Button
          color={
            invalidPrice ||
            invalidReason ||
            paymentMethod === "" ||
            paymentMethod === undefined ||
            paymentMethod === null ||
            paymentMethodType === undefined ||
            paymentMethodType === null ||
            makeSureUserDoesntSpamChargeButton
              ? ButtonColor.GRAY
              : ButtonColor.GREEN
          }
          size={ButtonSize.DEFAULT}
          onClick={handleCharge}
          disabled={
            status === PageStatus.LOADING ||
            invalidPrice ||
            invalidReason ||
            paymentMethod === "" ||
            paymentMethod === undefined ||
            paymentMethod === null ||
            paymentMethodType === undefined ||
            paymentMethodType === null ||
            makeSureUserDoesntSpamChargeButton
          }
        >
          Charge
        </Button>
      </div>
    </DialogContent>
  );
}
